import { CartEvents, cartEventEmitter } from "@made-people/grease-cart";
import { useEffect } from "react";
import { useMmGtm } from "../mm-gtm-context";
import { gtmGetCartItem } from "../mm-gtm-helpers";

export const MmGtmEventRemoveFromCart = () => {
  const { pusher, frontendContext } = useMmGtm();

  useEffect(() => {
    /**
     * Remove From Cart event.
     */
    const onRemoveFromCart = (params: CartEvents["removeFromCart"]) => {
      const removedItem = params.payload.removedItem;
      if (!removedItem) {
        console.warn(
          `GTM Event Remove From Cart: No removed item passed, not pushing event`,
          { items: params.cart.items },
        );
        return;
      }

      const products = params.cart.items.map((item) =>
        gtmGetCartItem(item, item.quantity, frontendContext),
      );

      if (removedItem.quantity === 0) {
        products.push(gtmGetCartItem(removedItem, 0, frontendContext));
      }

      pusher({
        event: "removeFromCart",
        eventLabel: removedItem.product.base.content.name,
        ecommerce: {
          remove: {
            products: [
              gtmGetCartItem(
                removedItem,
                removedItem.quantity,
                frontendContext,
              ),
            ],
          },
          cartItems: {
            products,
          },
        },
      });
    };

    /**
     * Update Quantity event, passes only the updated item if the
     * quantity is negative
     */
    const onUpdatedCart = (params: CartEvents["updateQuantity"]) => {
      const oldQuantity = params.oldCart?.items.find(
        (item) => item.lineId === params.payload.lineId,
      )?.quantity;
      if (oldQuantity === undefined) {
        return;
      }

      const updatedItem = params.cart.items.find(
        (item) => item.lineId === params.payload.lineId,
      );
      if (!updatedItem) {
        console.warn(
          `GTM Event Update Quantity: No item with ID '${params.payload.lineId}' found in cart, not pushing event`,
          { items: params.cart.items },
        );
        return;
      }

      const qtyDiff = params.payload.quantity - oldQuantity;
      if (qtyDiff < 0) {
        onRemoveFromCart({
          cart: params.cart,
          payload: {
            lineId: params.payload.lineId,
            quantity: Math.abs(qtyDiff),
            removedItem: updatedItem,
          },
        });
      }
    };

    cartEventEmitter.on("removeFromCart", onRemoveFromCart);
    cartEventEmitter.on("updateQuantity", onUpdatedCart);
    return () => {
      cartEventEmitter.off("removeFromCart", onRemoveFromCart);
      cartEventEmitter.off("updateQuantity", onUpdatedCart);
    };
  }, []);

  return null;
};
